import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import ApiService from '../../services/api';
import { Fund, FundBalance, FundPosition, FundOrder } from './types';
import FundSourcesSection from './FundSourcesSection';
import FundOrderModal from './FundOrderModal';
import { getToken } from '../../utils/auth';
import { useNotification } from '../../context/NotificationContext';
import ActivityDetailsModal from './ActivityDetailsModal';
import AddManagerModal from './AddManagerModal';

/**
 * AI SUMMARY:
 * The FundDetails component displays detailed information about a selected fund.
 * Features:
 * - Displays fund name, description, and other metadata
 * - Shows fund balance with source breakdowns
 * - Lists fund managers with their information
 * - Displays fund positions in a table with profit/loss calculations
 * - Provides fund source management through a dedicated tab
 * - Shows fund orders in a dedicated tab with ability to create new orders
 * - Handles loading states and error conditions
 * - Provides navigation back to the fund list
 *
 * The component fetches fund details, balance, and positions from the API
 * and displays them in a structured layout with proper error handling.
 */

/**
 * Extracts and formats the balance amount from a source balance object
 */
const getSourceBalanceAmount = (sourceBalance: Record<string, any>): string => {
  // If totalCash exists in the source balance, use it
  if (typeof sourceBalance.totalCash === 'number') {
    return sourceBalance.totalCash.toFixed(2);
  }

  // For Tradier accounts
  if (
    sourceBalance.sourceType === 'tradier' &&
    typeof sourceBalance.totalEquity === 'number'
  ) {
    return sourceBalance.totalEquity.toFixed(2);
  }

  // For other types, try to find a numeric value
  const possibleAmounts = ['totalEquity', 'totalCash', 'usdc', 'eth'];
  for (const key of possibleAmounts) {
    if (sourceBalance[key] !== undefined) {
      const value = sourceBalance[key];
      if (typeof value === 'number') {
        return value.toFixed(2);
      } else if (typeof value === 'string' && !isNaN(parseFloat(value))) {
        return parseFloat(value).toFixed(2);
      }
    }
  }

  return '0.00';
};

/**
 * Calculates the total cash from a fund balance object
 */
const calculateTotalCash = (balance: FundBalance): string => {
  // If totalCash is provided and not null, use it
  if (balance.totalCash !== null && balance.totalCash !== undefined) {
    return balance.totalCash.toFixed(2);
  }

  // Otherwise calculate from balances
  let total = 0;

  if (balance.balances && Array.isArray(balance.balances)) {
    balance.balances.forEach((sourceBalance) => {
      // For Tradier accounts
      if (
        sourceBalance.sourceType === 'tradier' &&
        typeof sourceBalance.totalEquity === 'number'
      ) {
        total += sourceBalance.totalEquity;
        return;
      }

      // For other types
      const possibleAmounts = ['totalEquity', 'totalCash', 'usdc', 'eth'];
      for (const key of possibleAmounts) {
        if (sourceBalance[key] !== undefined) {
          const value = sourceBalance[key];
          if (typeof value === 'number') {
            total += value;
            break;
          } else if (typeof value === 'string' && !isNaN(parseFloat(value))) {
            total += parseFloat(value);
            break;
          }
        }
      }
    });
  }

  return total.toFixed(2);
};

// Tab types for the fund details page
type TabType =
  | 'overview'
  | 'sources'
  | 'positions'
  | 'orders'
  | 'activities'
  | 'managers';

// Add new interfaces
export interface FundActivity {
  id: string;
  type: string;
  data: any;
  createdAt: string;
  user?: {
    id: string;
    name: string;
    email: string;
    image?: string;
  };
}

export interface FundActivitiesResponse {
  activities: FundActivity[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

const FundDetails = () => {
  const { fundId } = useParams<{ fundId: string }>();
  const [fund, setFund] = useState<Fund | null>(null);
  const [balance, setBalance] = useState<FundBalance | null>(null);
  const [positions, setPositions] = useState<FundPosition[]>([]);
  const [orders, setOrders] = useState<FundOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const { showNotification } = useNotification();
  const [activities, setActivities] = useState<FundActivity[]>([]);
  const [activitiesPage, setActivitiesPage] = useState(1);
  const [activitiesTotalPages, setActivitiesTotalPages] = useState(1);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState<FundActivity | null>(
    null,
  );
  const [isActivityModalOpen, setIsActivityModalOpen] = useState(false);
  const [isAddManagerModalOpen, setIsAddManagerModalOpen] = useState(false);

  const fetchFundData = async () => {
    if (!fundId) return;

    try {
      const token = getToken();
      if (!token) {
        showNotification('No API token found', 'error');
        setLoading(false);
        return;
      }

      const apiService = new ApiService(token);

      try {
        // Find the fund in the list of funds
        const fundsResponse = await apiService.getFunds();
        if (Array.isArray(fundsResponse)) {
          const selectedFund = fundsResponse.find((f) => f.id === fundId);
          if (selectedFund) {
            setFund(selectedFund);
          } else {
            showNotification('Fund not found', 'error');
            setLoading(false);
            return;
          }
        } else {
          showNotification('Invalid response from server', 'error');
          setLoading(false);
          return;
        }

        // Fetch fund balance
        try {
          const balanceResponse = await apiService.getFundBalance(fundId);
          if (
            balanceResponse &&
            typeof balanceResponse === 'object' &&
            'fundId' in balanceResponse
          ) {
            setBalance(balanceResponse as unknown as FundBalance);
          } else {
            console.error('Invalid balance response format:', balanceResponse);
            showNotification('Failed to fetch fund balance', 'error');
            setBalance(null);
          }
        } catch (balanceError) {
          console.error('Error fetching balance:', balanceError);
          showNotification('Failed to fetch fund balance', 'error');
        }

        // Fetch fund positions
        try {
          const positionsResponse = await apiService.getFundPositions(fundId);
          setPositions(
            Array.isArray(positionsResponse) ? positionsResponse : [],
          );
        } catch (positionsError) {
          console.error('Error fetching positions:', positionsError);
          showNotification('Failed to fetch fund positions', 'error');
          setPositions([]);
        }

        // Fetch fund orders
        try {
          const ordersResponse = await apiService.getFundOrders(fundId);
          setOrders(Array.isArray(ordersResponse) ? ordersResponse : []);
        } catch (ordersError) {
          console.error('Error fetching orders:', ordersError);
          showNotification('Failed to fetch fund orders', 'error');
          setOrders([]);
        }

        // Fetch fund activities
        try {
          setActivitiesLoading(true);
          const response = await apiService.getFundActivities(
            fundId,
            activitiesPage,
          );

          // Axios response structure has a data property
          if (response) {
            // The actual API response is in response.activities
            const activitiesData = response as any;

            if (activitiesData.activities) {
              setActivities(activitiesData.activities);
              setActivitiesTotalPages(
                activitiesData.pagination?.totalPages || 1,
              );
            } else {
              console.error(
                'Activities data missing in response:',
                activitiesData,
              );
              showNotification('Invalid activities data format', 'error');
              setActivities([]);
            }
          } else {
            console.error('Invalid activities response format:', response);
            showNotification('Failed to fetch fund activities', 'error');
            setActivities([]);
          }
        } catch (activitiesError) {
          console.error('Error fetching activities:', activitiesError);
          showNotification('Failed to fetch fund activities', 'error');
          setActivities([]);
        } finally {
          setActivitiesLoading(false);
        }
      } catch (apiError) {
        console.error('API error:', apiError);
        showNotification('Failed to fetch fund details', 'error');
      }

      setLoading(false);
    } catch (error) {
      console.error('Error in fund details:', error);
      showNotification('Failed to fetch fund details', 'error');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFundData();
  }, [fundId]);

  useEffect(() => {
    if (activeTab === 'activities') {
      fetchFundData();
    }
  }, [activeTab, activitiesPage]);

  const handleSourcesChange = () => {
    fetchFundData();
  };

  const handleOrderCreated = () => {
    fetchFundData();
  };

  const handleCancelOrder = async (orderId: number) => {
    try {
      const token = getToken();
      if (!token) {
        showNotification('No API token found', 'error');
        return;
      }

      const apiService = new ApiService(token);
      await apiService.cancelOrder(fundId || '', orderId);
      showNotification('Order cancelled successfully', 'success');

      // Refresh orders
      fetchFundData();
    } catch (error) {
      console.error('Error cancelling order:', error);
      showNotification('Failed to cancel order', 'error');
    }
  };

  // Add function to handle opening the activity details modal
  const handleViewActivityDetails = (activity: FundActivity) => {
    setSelectedActivity(activity);
    setIsActivityModalOpen(true);
  };

  // Add function to handle manager added
  const handleManagerAdded = () => {
    fetchFundData();
  };

  if (loading) {
    return <div className="text-center py-8">Loading fund details...</div>;
  }

  if (error) {
    return <div className="text-center py-8 text-red-500">{error}</div>;
  }

  if (!fund) {
    return <div className="text-center py-8">Fund not found</div>;
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">{fund.name}</h1>
        <Link
          to="/fund/list"
          className="bg-gray-200 px-4 py-2 rounded hover:bg-gray-300"
        >
          Back to Funds
        </Link>
      </div>

      {fund.description && (
        <p className="mb-6 text-gray-600">{fund.description}</p>
      )}

      {/* Tabs Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('overview')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'overview'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('sources')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'sources'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Fund Sources
          </button>
          <button
            onClick={() => setActiveTab('positions')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'positions'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Positions
          </button>
          <button
            onClick={() => setActiveTab('orders')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'orders'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Orders
          </button>
          <button
            onClick={() => setActiveTab('activities')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'activities'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Activities
          </button>
          <button
            onClick={() => setActiveTab('managers')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'managers'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Managers
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Balance Card */}
          <div className="bg-white p-6 rounded shadow">
            <h2 className="text-xl font-semibold mb-4">Fund Balance</h2>
            {balance ? (
              <div>
                <div className="text-3xl font-bold text-green-600 mb-2">
                  ${calculateTotalCash(balance)}
                  <span className="text-sm text-gray-500">
                    {balance.currency || 'USD'}
                  </span>
                </div>
                <div className="text-sm text-gray-500">
                  Ledger Balance: $
                  {balance.ledger ? balance.ledger.toFixed(2) : '0.00'}
                </div>

                {balance.balances && balance.balances.length > 0 && (
                  <div className="mt-4">
                    <h3 className="font-semibold mb-2">Source Balances</h3>
                    <div className="space-y-2">
                      {balance.balances.map((sourceBalance, index) => (
                        <div key={index} className="flex justify-between">
                          <span>
                            {sourceBalance.sourceType} ({sourceBalance.sourceId}
                            )
                          </span>
                          <span className="font-medium">
                            ${getSourceBalanceAmount(sourceBalance)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p>No balance information available</p>
            )}
          </div>

          {/* Managers Card */}
          <div className="bg-white p-6 rounded shadow">
            <h2 className="text-xl font-semibold mb-4">Fund Managers</h2>
            {fund.managers && fund.managers.length > 0 ? (
              <div className="space-y-3">
                {fund.managers.map((manager) => (
                  <div key={manager.id} className="flex items-center">
                    {manager.image ? (
                      <img
                        src={manager.image}
                        alt={manager.name}
                        className="w-10 h-10 rounded-full mr-3"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gray-300 mr-3 flex items-center justify-center">
                        {manager.name.charAt(0)}
                      </div>
                    )}
                    <div>
                      <div className="font-medium">{manager.name}</div>
                      <div className="text-sm text-gray-500">
                        {manager.email}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p>No managers assigned</p>
            )}
          </div>
        </div>
      )}

      {/* Fund Sources Tab */}
      {activeTab === 'sources' && (
        <FundSourcesSection
          fundId={fundId || ''}
          sources={fund.fundSources}
          onSourcesChange={handleSourcesChange}
        />
      )}

      {/* Positions Tab */}
      {activeTab === 'positions' && (
        <div className="bg-white p-6 rounded shadow">
          <h2 className="text-xl font-semibold mb-4">Fund Positions</h2>
          {positions.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Symbol
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Quantity
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cost Basis
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Current Value
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      P/L
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {positions.map((position) => (
                    <tr key={position.symbol}>
                      <td className="px-6 py-4 whitespace-nowrap font-medium">
                        {position.symbol}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {position.quantity}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        $
                        {position.costBasis
                          ? position.costBasis.toFixed(2)
                          : '0.00'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        $
                        {position.currentValue
                          ? position.currentValue.toFixed(2)
                          : '0.00'}
                      </td>
                      <td
                        className={`px-6 py-4 whitespace-nowrap ${
                          position.profitLoss >= 0
                            ? 'text-green-600'
                            : 'text-red-600'
                        }`}
                      >
                        $
                        {position.profitLoss
                          ? position.profitLoss.toFixed(2)
                          : '0.00'}
                        (
                        {position.profitLossPercentage
                          ? position.profitLossPercentage.toFixed(2)
                          : '0.00'}
                        %)
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p>No positions in this fund</p>
          )}
        </div>
      )}

      {/* Orders Tab */}
      {activeTab === 'orders' && (
        <div className="bg-white p-6 rounded shadow">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Fund Orders</h2>
            <button
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
              onClick={() => setIsOrderModalOpen(true)}
            >
              New Order
            </button>
          </div>

          {orders.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Symbol
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Side
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Quantity
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Price
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Source
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {orders.map((order) => (
                    <tr key={order.id}>
                      <td className="px-6 py-4 whitespace-nowrap font-medium">
                        {order.symbol}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium ${
                            order.side === 'buy'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {order.side.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {order.type.replace('_', ' ').toUpperCase()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {order.quantity}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        ${(order.price ?? 0).toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium ${
                            order.status === 'filled'
                              ? 'bg-green-100 text-green-800'
                              : order.status === 'open'
                                ? 'bg-blue-100 text-blue-800'
                                : order.status === 'canceled'
                                  ? 'bg-gray-100 text-gray-800'
                                  : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {order.status.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {order.sourceType} ({order.sourceId})
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {new Date(order.create_date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {['open', 'pending'].includes(order.status) && (
                          <button
                            className="text-red-600 hover:text-red-900 inline-flex items-center"
                            onClick={() => {
                              if (
                                window.confirm(
                                  'Are you sure you want to cancel this order?',
                                )
                              ) {
                                handleCancelOrder(Number(order.id));
                              }
                            }}
                            title="Cancel Order"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-5 w-5"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                clipRule="evenodd"
                              />
                            </svg>
                            <span className="ml-1">Cancel</span>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p>No orders for this fund</p>
          )}
        </div>
      )}

      {/* Activities Tab */}
      {activeTab === 'activities' && (
        <div className="bg-white p-6 rounded shadow">
          <h2 className="text-xl font-semibold mb-4">Fund Activities</h2>
          {activitiesLoading ? (
            <div className="text-center py-8">Loading activities...</div>
          ) : activities.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {activities.map((activity) => (
                      <tr key={activity.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                            {activity.type.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {activity.user ? (
                            <div className="flex items-center">
                              {activity.user.image ? (
                                <img
                                  src={activity.user.image}
                                  alt={activity.user.name}
                                  className="w-8 h-8 rounded-full mr-2"
                                />
                              ) : (
                                <div className="w-8 h-8 rounded-full bg-gray-300 mr-2 flex items-center justify-center">
                                  {activity.user.name.charAt(0)}
                                </div>
                              )}
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {activity.user.name}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {activity.user.email}
                                </div>
                              </div>
                            </div>
                          ) : (
                            <span className="text-gray-500">System</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(activity.createdAt).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => handleViewActivityDetails(activity)}
                            className="text-blue-600 hover:text-blue-900 inline-flex items-center"
                            title="View Details"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-5 w-5 mr-1"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                              <path
                                fillRule="evenodd"
                                d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 flex justify-between items-center">
                <div className="text-sm text-gray-700">
                  Page {activitiesPage} of {activitiesTotalPages}
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setActivitiesPage((p) => Math.max(1, p - 1))}
                    disabled={activitiesPage === 1}
                    className="px-3 py-1 rounded bg-gray-200 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() =>
                      setActivitiesPage((p) =>
                        Math.min(activitiesTotalPages, p + 1),
                      )
                    }
                    disabled={activitiesPage === activitiesTotalPages}
                    className="px-3 py-1 rounded bg-gray-200 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          ) : (
            <p>No activities found</p>
          )}
        </div>
      )}

      {/* Managers Tab */}
      {activeTab === 'managers' && (
        <div className="bg-white p-6 rounded shadow">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Fund Managers</h2>
            <button
              onClick={() => setIsAddManagerModalOpen(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Add Manager
            </button>
          </div>

          {fund?.managers && fund.managers.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {fund.managers.map((manager) => (
                <div
                  key={manager.id}
                  className="border rounded-lg p-4 flex items-center"
                >
                  {manager.image ? (
                    <img
                      src={manager.image}
                      alt={manager.name}
                      className="w-12 h-12 rounded-full mr-4"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-gray-300 mr-4 flex items-center justify-center text-xl font-semibold">
                      {manager.name.charAt(0)}
                    </div>
                  )}
                  <div>
                    <h3 className="font-medium text-gray-900">
                      {manager.name}
                    </h3>
                    <p className="text-sm text-gray-500">{manager.email}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500">No managers found</p>
          )}
        </div>
      )}

      {/* Activity Details Modal */}
      <ActivityDetailsModal
        activity={selectedActivity}
        isOpen={isActivityModalOpen}
        onClose={() => setIsActivityModalOpen(false)}
      />

      {/* Order Modal */}
      {fund && (
        <FundOrderModal
          isOpen={isOrderModalOpen}
          onClose={() => setIsOrderModalOpen(false)}
          fundId={fundId || ''}
          sources={fund.fundSources}
          onOrderCreated={handleOrderCreated}
        />
      )}

      {/* Add Manager Modal */}
      <AddManagerModal
        fundId={fundId || ''}
        isOpen={isAddManagerModalOpen}
        onClose={() => setIsAddManagerModalOpen(false)}
        onManagerAdded={handleManagerAdded}
      />
    </div>
  );
};

export default FundDetails;
